import { useMemo, type ComponentProps } from 'react'
import { useParams, useNavigate, Navigate } from 'react-router'
import { useThreadsInfinite, useReservation, useProperty } from '@/hooks/useApi'
import { useThreadOrchestration } from '@/hooks/useThreadOrchestration'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { ConversationList } from '@/components/messages/conversation-list'
import { ThreadPane } from '@/components/messages/thread-pane'
import { ThreadHeader } from '@/components/messages/thread-header'
import { ReservationSidebar } from '@/components/messages/reservation-sidebar'
import { Composer } from '@/components/messages/composer'
import { getGuestDisplayName, getPropertyDisplayName } from '@/lib/format-utils'

function FullPageSkeleton() {
  return (
    <div className="grid h-[calc(100vh-160px)] grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
      <LoadingSkeleton className="h-full w-full" />
      <LoadingSkeleton className="h-full w-full" />
      <LoadingSkeleton className="h-full w-full" />
    </div>
  )
}

/**
 * Admin portal-wide Inbox - the same three-pane conversation list / thread /
 * reservation-sidebar UX as the per-property Messages page
 * (src/pages/property-messages.jsx), aggregating conversations across ALL
 * properties instead of one. Deliberately has no List/Calendar toggle (not
 * needed at this scope) and no per-property sync control (reservations sync
 * on the nightly admin-api cron + Uplisting webhooks regardless of which
 * page is open - see root CLAUDE.md's "Uplisting Sync Rules").
 *
 * Shares its thread-driving logic (suggestion polling, mark-read,
 * send/regenerate) with property-messages.jsx via useThreadOrchestration -
 * see that hook's own docs for why this is extracted rather than
 * duplicated.
 *
 * Handles both `/inbox` (defaults to the most recent conversation across
 * every property, or the empty-inbox state below) and `/inbox/:threadId`
 * (deep link to a specific thread, fetched directly rather than depending
 * on it being present in the paginated list - a thread older than the
 * first page would not have loaded there).
 */
export function InboxPage() {
  const { threadId } = useParams()
  const navigate = useNavigate()

  // Same query key/args ConversationList uses internally by default (no
  // limit, needsReply=false) - shares its cache entry rather than issuing a
  // second request, and gives us the most-recent thread for the
  // no-threadId default-selection redirect below.
  const listQuery = useThreadsInfinite({})
  const listItems = useMemo(
    () => listQuery.data?.pages.flatMap(page => page.items) ?? [],
    [listQuery.data]
  )
  const mostRecentThreadId = listItems[0]?.threadId ?? null

  const {
    threadQuery,
    threadData,
    messages,
    setThreadScrollNode,
    liveMessage,
    suggestion,
    isGenerating,
    isPollExhausted,
    isSendConfirmationExhausted,
    hasActivePendingSendForLiveMessage,
    isSubmittingGenerate,
    isDismissingNeedsReply,
    handleSend,
    handleResendPendingMessage,
    lastInlineResendSentKey,
    handleRegenerate,
    handleCheckAgain,
    handleDismissNeedsReply,
  } = useThreadOrchestration({
    threadId,
    listQueryKeyPrefix: ['threads-page'],
  })

  const reservationIdFromThread = threadData?.reservation_id ?? null
  const reservationFromThreadQuery = useReservation(reservationIdFromThread)

  // A deep-linked/uncached thread has no corresponding conversation-list row to show which
  // property it belongs to (Codex PR review) - unlike property-messages.jsx, this page has no
  // other property context on screen at all, so it's fetched directly from the thread's own
  // property_id and shown in the thread header.
  const propertyIdFromThread = threadData?.property_id ?? null
  const propertyFromThreadQuery = useProperty(propertyIdFromThread)
  const propertyDisplayName = propertyIdFromThread
    ? getPropertyDisplayName(
        // GetPropertyResponse's `cover_photo_url` is `string | null | undefined` (a nullable
        // Uplisting-sourced field per the contracts schema) while getPropertyDisplayName's own
        // PropertyLike param predates that and only allows `string | undefined` - format-utils.ts
        // isn't part of this conversion, so the mismatch is bridged here rather than widened there.
        propertyFromThreadQuery.data as Parameters<typeof getPropertyDisplayName>[0],
        propertyIdFromThread
      )
    : null

  const handleSelectThread = (item?: { threadId?: string } | null) => {
    if (!item?.threadId) return
    navigate(`/inbox/${item.threadId}`)
  }

  // --- Deep-linked thread route ---
  if (threadId) {
    // The three-pane shell (conversation list, header, sidebar) stays mounted across a
    // thread-to-thread navigation - only the message pane below shows its own scoped
    // loading/error state while threadQuery fetches the newly-selected thread. Mirrors the
    // identical fix in property-messages.jsx (Codex PR review on this page: an earlier
    // version returned a FullPageSkeleton/ErrorState here instead, which replaced this
    // entire tree - dropping ConversationList, along with its own internal "Needs reply"
    // filter selection - and blanking the sidebar - every time an operator clicked a thread
    // that wasn't already cached).
    const guestDisplayName = reservationFromThreadQuery.data
      ? getGuestDisplayName(
          // Same nullable-vs-undefined mismatch as getPropertyDisplayName above:
          // GetReservationResponse models `guest_name`/`external` more permissively (nullable,
          // and `external` is `unknown`) than getGuestDisplayName's own ReservationLike param.
          reservationFromThreadQuery.data as Parameters<typeof getGuestDisplayName>[0]
        )
      : 'Guest'

    return (
      <div className="grid h-[calc(100vh-160px)] grid-cols-1 gap-4 lg:grid-cols-[320px_1fr_320px]">
        <div className="overflow-hidden rounded-card border border-border bg-surface">
          <ConversationList selectedThreadId={threadId} onSelectThread={handleSelectThread} />
        </div>
        <div className="flex flex-col overflow-hidden rounded-card border border-border bg-surface">
          <ThreadHeader
            reservation={
              // ThreadHeader's local Reservation type predates the real, more permissive
              // (nullable) GetReservationResponse contract shape - see the comment above.
              reservationFromThreadQuery.data as ComponentProps<typeof ThreadHeader>['reservation']
            }
            guestDisplayName={guestDisplayName}
            propertyName={propertyDisplayName ?? undefined}
          />
          <div
            ref={setThreadScrollNode}
            data-testid="thread-scroll-container"
            className="flex-1 overflow-y-auto p-4"
          >
            {threadQuery.isLoading ? (
              <div className="flex h-full items-center justify-center gap-2 text-sm text-textMuted">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading conversation…
              </div>
            ) : threadQuery.error ? (
              <ErrorState error={threadQuery.error} onRetry={threadQuery.refetch} />
            ) : (
              <ThreadPane
                messages={messages}
                guestDisplayName={guestDisplayName}
                onResendPendingMessage={handleResendPendingMessage}
                isSendConfirmationExhausted={isSendConfirmationExhausted}
                onCheckAgainSendConfirmation={handleCheckAgain}
              />
            )}
          </div>
          {threadQuery.isLoading ? (
            // Composer stays unmounted (rather than rendered-but-disabled) while the thread
            // is still loading - a disabled textarea is still exposed as an interactive
            // textbox, so a visible-but-not-yet-usable composer here would be misleading.
            <div className="border-t border-border p-3">
              <LoadingSkeleton className="h-20 w-full" />
            </div>
          ) : threadQuery.error ? (
            <div className="border-t border-border p-3 text-center text-xs text-textFaint">
              Reply unavailable until the conversation loads
            </div>
          ) : (
            <>
              {liveMessage && (!suggestion || isGenerating || threadData?.needs_reply) && (
                <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2 text-xs text-textMuted">
                  <span>
                    {!suggestion || isGenerating
                      ? isPollExhausted
                        ? 'Still working on a reply - check again or try once more.'
                        : isGenerating
                          ? 'Generating a suggested reply…'
                          : 'No suggestion yet'
                      : null}
                  </span>
                  <div className="flex items-center gap-2">
                    {(!suggestion || isGenerating) && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          isPollExhausted ? handleCheckAgain() : handleRegenerate(null)
                        }
                        disabled={(isGenerating && !isPollExhausted) || isSubmittingGenerate}
                      >
                        <RefreshCw
                          className={`h-3.5 w-3.5 ${isGenerating && !isPollExhausted ? 'animate-spin' : ''}`}
                        />
                        {isPollExhausted ? 'Check again' : 'Generate suggestion'}
                      </Button>
                    )}
                    {threadData?.needs_reply && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleDismissNeedsReply}
                        disabled={isDismissingNeedsReply}
                      >
                        {isDismissingNeedsReply ? 'Marking…' : 'No reply needed'}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <Composer
                threadId={threadId}
                suggestion={suggestion}
                externallyResentIdempotencyKey={lastInlineResendSentKey}
                onSend={handleSend}
                onRegenerate={handleRegenerate}
                disabled={!liveMessage || isGenerating || hasActivePendingSendForLiveMessage}
              />
            </>
          )}
        </div>
        <div className="overflow-y-auto rounded-card border border-border bg-surface p-4">
          {reservationFromThreadQuery.isLoading ||
          // useReservation(null) is disabled and never reports isLoading - without this, an
          // uncached thread left reservationIdFromThread null until threadData resolved, so
          // this branch fell straight to the else below and rendered ReservationSidebar with
          // an undefined reservation instead of a loading state. Mirrors the identical fix
          // in property-messages.jsx.
          (!reservationIdFromThread && threadQuery.isLoading) ? (
            <LoadingSkeleton className="h-64 w-full" />
          ) : reservationFromThreadQuery.error ||
            (!reservationIdFromThread && threadQuery.error) ? (
            <ErrorState
              error={reservationFromThreadQuery.error || threadQuery.error}
              onRetry={
                reservationFromThreadQuery.error
                  ? reservationFromThreadQuery.refetch
                  : threadQuery.refetch
              }
            />
          ) : (
            <ReservationSidebar
              reservation={
                // Same nullable-vs-undefined mismatch as ThreadHeader above.
                reservationFromThreadQuery.data as ComponentProps<
                  typeof ReservationSidebar
                >['reservation']
              }
            />
          )}
        </div>
      </div>
    )
  }

  // --- Base /inbox route: resolve a default selection ---

  if (listQuery.isLoading) {
    return <FullPageSkeleton />
  }

  if (listQuery.error && listItems.length === 0) {
    return <ErrorState error={listQuery.error} onRetry={listQuery.refetch} />
  }

  if (mostRecentThreadId) {
    return <Navigate to={`/inbox/${mostRecentThreadId}`} replace />
  }

  // No conversations anywhere yet - a genuinely rare, brand-new-deployment
  // case. Unlike property-messages.jsx there is no cheap cross-property
  // reservations fallback to fall back to here, so this is the one empty
  // state (see docs/archive/messages-redesign-pr-slices.md's "Resolved decisions"
  // #2 for the per-property equivalent this deliberately doesn't replicate
  // at global scope).
  return <EmptyState message="No conversations yet." />
}
